import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../lib/api';
import { qk } from './keys';
import type { Subject } from '../types/api';
import type {
  LearningPlan,
  PlanAdjustmentCandidate,
  PlanAdjustmentInput,
  PlanAdjustmentPreview,
  PlanChange,
  PlanClosure,
  PlanOffer,
  PlanOfferInput,
  PlanTeacherCandidate,
  TeachingRequest,
  PlanAudit,
  AdminRequestPlanInput,
  PlanQueue,
  PlanQueueQuery,
  PlanWorkSummary,
} from '../types/learning-plans';

interface PlanScreenData {
  requests: LearningPlan[];
  offers: PlanOffer[];
  changes: PlanChange[];
  closures: PlanClosure[];
  subjects: { id: string; nameEn: string; nameAr?: string; slug: string }[];
  grades: { id: string; nameEn: string; nameAr?: string; levelId: string }[];
  audit: PlanAudit[];
  queue?: PlanQueue;
}

export function useLearningPlans(tab: string, filters: PlanQueueQuery) {
  return useQuery({
    queryKey: qk.planScreen(tab, tab === 'queue' ? filters : {}),
    refetchInterval: 30000,
    placeholderData: (previous, previousQuery) => previousQuery?.queryKey[3] === tab ? previous : undefined,
    queryFn: async (): Promise<PlanScreenData> => {
      const data: PlanScreenData = { requests: [], offers: [], changes: [], closures: [], subjects: [], grades: [], audit: [] };
      if (tab === 'queue') {
        data.queue = await api.get<PlanQueue>('/admin/learning-plans/queue', filters);
        data.requests = data.queue.data;
      } else if (tab === 'closures') {
        data.closures = await api.get<PlanClosure[]>('/learning-plan-closures');
      } else if (tab === 'changes') {
        data.changes = await api.get<PlanChange[]>('/admin/learning-plan-changes');
        // A support case may reference a plan older than the legacy queue's limit.
        data.requests = await Promise.all([...new Set(data.changes.map((c) => c.requestId))]
          .map((id) => api.get<LearningPlan>(`/admin/learning-plans/${id}`)));
      } else if (tab === 'audit') {
        [data.audit, data.requests, data.offers, data.changes] = await Promise.all([
          api.get<PlanAudit[]>('/admin/learning-plans/audit'),
          api.get<LearningPlan[]>('/admin/learning-plans'),
          api.get<PlanOffer[]>('/admin/learning-plans/offers'),
          api.get<PlanChange[]>('/admin/learning-plan-changes'),
        ]);
      } else if (tab === 'offers') {
        [data.offers, data.requests, data.subjects] = await Promise.all([
          api.get<PlanOffer[]>('/admin/learning-plans/offers'),
          api.get<LearningPlan[]>('/admin/learning-plans'),
          api.get<PlanScreenData['subjects']>('/catalog/subjects', { activeOnly: false }),
        ]);
        const countries = [...new Set(['SA', ...data.offers.map((o) => o.countryCode)])];
        data.grades = (await Promise.all(countries.map((country) =>
          api.get<PlanScreenData['grades']>('/catalog/grades', { country, activeOnly: false })))).flat();
      }
      return data;
    },
  });
}

export function usePlanSubjects(enabled: boolean) {
  return useQuery({
    queryKey: qk.planSubjects,
    queryFn: () => api.get<PlanScreenData['subjects']>('/catalog/subjects', { activeOnly: false }),
    enabled,
  });
}

export function usePlanGroup(id: string) {
  return useQuery({
    queryKey: qk.planGroup(id),
    queryFn: () => api.get<LearningPlan[]>(`/admin/learning-plans/${id}/group`),
    refetchInterval: 30000,
  });
}

export function useStudentPlanOffers(studentId: string): UseQueryResult<PlanOffer[], Error> {
  return useQuery({
    queryKey: qk.studentPlanOffers(studentId),
    queryFn: () => api.get<PlanOffer[]>(`/admin/students/${studentId}/learning-plan-offers`),
    enabled: Boolean(studentId),
  });
}

export function useCreateStudentPlan(): UseMutationResult<LearningPlan, Error, { studentId: string; input: AdminRequestPlanInput }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, input }) => api.post<LearningPlan>(`/admin/students/${studentId}/learning-plans`, input),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: qk.learningPlans }); },
  });
}
export function useLearningPlanWork(): UseQueryResult<PlanWorkSummary, Error> {
  return useQuery({
    queryKey: qk.planWork,
    refetchInterval: 30000,
    queryFn: () => api.get<PlanWorkSummary>('/admin/learning-plans/work'),
  });
}

export function useTeachingResponses(id: string) {
  return useQuery({
    queryKey: [...qk.learningPlans, 'teachers', id],
    queryFn: () => api.get<TeachingRequest[]>(`/admin/learning-plans/${id}/teachers`),
    enabled: Boolean(id),
    refetchInterval: 10000,
  });
}
export function useTeacherCandidates(id: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.teacherCandidates(id),
    queryFn: () => api.get<PlanTeacherCandidate[]>(`/admin/learning-plans/${id}/teacher-candidates`),
    enabled,
  });
}
export function useLearningPlanMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body, method = 'POST' }: { path: string; body?: unknown; method?: 'POST' | 'DELETE' }) =>
      method === 'DELETE' ? api.delete<unknown>(path) : api.post<unknown>(path, body),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.learningPlans }),
        qc.invalidateQueries({ queryKey: qk.adminSessionsAll }),
        qc.invalidateQueries({ queryKey: qk.usersAll }),
        qc.invalidateQueries({ queryKey: qk.stats }),
      ]);
    },
  });
}

export function useReplacementCandidates(id: string, target: { targetKind: 'lesson' | 'change'; targetId: string; startTime?: string }, enabled: boolean): UseQueryResult<{ tutorId: string; tutorName: string }[], Error> {
  return useQuery({
    queryKey: qk.replacementCandidates(id, target),
    queryFn: () => api.post<{ tutorId: string; tutorName: string }[]>(`/admin/learning-plans/${id}/replacement-candidates`, target),
    enabled,
  });
}

// Preview and candidates are reads that happen to take a body; keyed by the
// whole input so editing a field shows that input's result, never a stale one.
export function useAdjustmentPreview(id: string, input: PlanAdjustmentInput | null): UseQueryResult<PlanAdjustmentPreview, Error> {
  return useQuery({
    queryKey: qk.adjustmentPreview(id, input ?? {}),
    queryFn: () => api.post<PlanAdjustmentPreview>(`/admin/learning-plans/${id}/adjustments/preview`, input),
    enabled: input !== null,
    retry: false,
  });
}

export function useAdjustmentCandidates(id: string, input: PlanAdjustmentInput | null): UseQueryResult<PlanAdjustmentCandidate[], Error> {
  return useQuery({
    queryKey: qk.adjustmentCandidates(id, input ?? {}),
    queryFn: () => api.post<PlanAdjustmentCandidate[]>(`/admin/learning-plans/${id}/adjustments/candidates`, input),
    enabled: input !== null,
    retry: false,
  });
}

export function useApplyAdjustment(): UseMutationResult<LearningPlan, Error, { id: string; input: PlanAdjustmentInput }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PlanAdjustmentInput }) =>
      api.post<LearningPlan>(`/admin/learning-plans/${id}/adjustments`, { ...input, familyAgreed: true }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.learningPlans }),
        qc.invalidateQueries({ queryKey: qk.adminSessionsAll }),
      ]);
    },
  });
}

interface OfferCatalog {
  subjects: Subject[];
  countries: { code: string; nameEn: string; nameAr?: string }[];
  grades: { id: string; levelId: string; nameEn: string; nameAr?: string }[];
  levels: { id: string; slug: string; nameEn: string; nameAr?: string }[];
}

export function useOfferCatalog(country: string): UseQueryResult<OfferCatalog, Error> {
  return useQuery({
    queryKey: qk.offerCatalog(country),
    queryFn: async () => {
      const [subjects, countries, grades, levels] = await Promise.all([
        api.get<Subject[]>('/catalog/subjects', { country }),
        api.get<{ code: string; nameEn: string; nameAr?: string }[]>('/catalog/countries', { registrationOpen: true }),
        api.get<{ id: string; levelId: string; nameEn: string; nameAr?: string }[]>('/catalog/grades', { country }),
        api.get<{ id: string; slug: string; nameEn: string; nameAr?: string }[]>('/catalog/levels', { country }),
      ]);
      return { subjects, countries, grades, levels };
    },
  });
}

interface SavedOffer { offer: PlanOffer; publicationError?: unknown }

export function useSavePlanOffer(): UseMutationResult<SavedOffer, Error, { offer: PlanOfferInput; publish: boolean }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ offer, publish }: { offer: PlanOfferInput; publish: boolean }): Promise<{ offer: PlanOffer; publicationError?: unknown }> => {
      const saved = await api.post<PlanOffer>('/admin/learning-plans/offers', offer);
      if (!publish) return { offer: saved };
      try {
        return { offer: await api.post<PlanOffer>(`/admin/learning-plans/offers/${saved.id}/publish`, { published: true }) };
      } catch (publicationError) {
        // The draft already exists: return its identity so retrying only publishes it.
        return { offer: saved, publicationError };
      }
    },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: qk.learningPlans }); },
  });
}
